import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import AdminAuth from '../components/AdminAuth';
import { sendTestEmail } from '../lib/resendEmailService';
import {
  Box,
  Text,
  VStack,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  Alert,
  AlertIcon,
  Spinner,
  Flex,
  useColorModeValue,
  useToast,
  Input,
  HStack
} from '@chakra-ui/react';
import {
  AddIcon,
  ViewIcon,
  DeleteIcon,
  TimeIcon,
  SettingsIcon,
  DownloadIcon
} from '@chakra-ui/icons';
import { listGalleries } from '../lib/galleryService';
import { getExpiredGalleries, getGalleriesExpiringSoon, getStorageStats, formatBytes } from '../lib/galleryCleanupService';
import type { DatabaseGallery } from '../lib/supabase';

interface DashboardStats {
  totalGalleries: number;
  activeGalleries: number;
  expiredGalleries: number;
  expiringSoon: number;
  storageUsed: string;
  expiredStorage: string;
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [recentGalleries, setRecentGalleries] = useState<DatabaseGallery[]>([]);
  const [testEmail, setTestEmail] = useState('');
  const [testingEmail, setTestingEmail] = useState(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const toast = useToast();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      const [galleries, expired, expiring, storageStats] = await Promise.all([
        listGalleries(),
        getExpiredGalleries(),
        getGalleriesExpiringSoon(7),
        getStorageStats()
      ]);

      const now = new Date();
      const activeCount = galleries.filter(g => 
        g.is_active && (!g.expires_at || new Date(g.expires_at) > now)
      ).length;

      const dashboardStats: DashboardStats = {
        totalGalleries: galleries.length,
        activeGalleries: activeCount,
        expiredGalleries: expired.length,
        expiringSoon: expiring.length,
        storageUsed: storageStats ? formatBytes(storageStats.total.size) : '0 MB',
        expiredStorage: storageStats ? formatBytes(storageStats.expired.size) : '0 MB'
      };

      setStats(dashboardStats);
      setRecentGalleries(galleries.slice(0, 5)); // Show 5 most recent
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast({
        title: 'Email required',
        description: 'Please enter an email address to test',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setTestingEmail(true);
    try {
      const result = await sendTestEmail(testEmail);
      if (result.success) {
        toast({
          title: 'Test email sent!',
          description: `Email sent successfully to ${testEmail}`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        setTestEmail('');
      } else {
        throw new Error(result.error || 'Failed to send test email');
      }
    } catch (error) {
      toast({
        title: 'Email failed',
        description: error instanceof Error ? error.message : 'Failed to send test email',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setTestingEmail(false);
    }
  };

  const getGalleryStatus = (gallery: DatabaseGallery) => {
    if (!gallery.is_active) {
      return <Badge colorScheme="gray">Inactive</Badge>;
    }
    
    if (gallery.expires_at) {
      const now = new Date();
      const expiryDate = new Date(gallery.expires_at);
      if (expiryDate < now) {
        return <Badge colorScheme="red">Expired</Badge>;
      }
      
      const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      if (daysUntilExpiry <= 7) {
        return <Badge colorScheme="orange">Expires in {daysUntilExpiry} days</Badge>;
      }
    }
    
    return <Badge colorScheme="green">Active</Badge>;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="50vh">
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <AdminAuth title="Admin Dashboard" description="Enter the admin password to access the dashboard">
      <Box maxW="container.xl" mx="auto" p={8} paddingTop={{ base: "80px", md: "20px" }}>
      <VStack spacing={8} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center">
          <Heading size="xl">Admin Dashboard</Heading>
          <Button
            as={RouterLink}
            to="/admin/gallery"
            colorScheme="blue"
            leftIcon={<AddIcon />}
            size="lg"
          >
            Create New Gallery
          </Button>
        </Flex>

        {/* Statistics */}
        {stats && (
          <SimpleGrid columns={{ base: 2, md: 4, lg: 8 }} spacing={6}>
            <Stat>
              <StatLabel>Total Galleries</StatLabel>
              <StatNumber>{stats.totalGalleries}</StatNumber>
              <StatHelpText>All time</StatHelpText>
            </Stat>
            <Stat>
              <StatLabel>Active</StatLabel>
              <StatNumber color="green.500">{stats.activeGalleries}</StatNumber>
              <StatHelpText>Currently accessible</StatHelpText>
            </Stat>
            <Stat>
              <StatLabel>Expired</StatLabel>
              <StatNumber color="red.500">{stats.expiredGalleries}</StatNumber>
              <StatHelpText>Past expiration</StatHelpText>
            </Stat>
            <Stat>
              <StatLabel>Expiring Soon</StatLabel>
              <StatNumber color="orange.500">{stats.expiringSoon}</StatNumber>
              <StatHelpText>Next 7 days</StatHelpText>
            </Stat>
            <Stat>
              <StatLabel>Storage Used</StatLabel>
              <StatNumber>{stats.storageUsed}</StatNumber>
              <StatHelpText>Total AWS S3</StatHelpText>
            </Stat>
            <Stat>
              <StatLabel>Expired Storage</StatLabel>
              <StatNumber color="red.500">{stats.expiredStorage}</StatNumber>
              <StatHelpText>Reclaimable space</StatHelpText>
            </Stat>
          </SimpleGrid>
        )}

        {/* Alerts */}
        {stats && stats.expiringSoon > 0 && (
          <Alert status="warning">
            <AlertIcon />
            <VStack align="start" spacing={1}>
              <Text fontWeight="bold">
                {stats.expiringSoon} galleries expiring in the next 7 days
              </Text>
              <Text fontSize="sm">
                Visit Gallery Cleanup to extend or manage expiring galleries.
              </Text>
            </VStack>
          </Alert>
        )}

        {stats && stats.expiredGalleries > 0 && (
          <Alert status="info">
            <AlertIcon />
            <Text>
              <strong>{stats.expiredGalleries} expired galleries</strong> using <strong>{stats.expiredStorage}</strong> of storage. 
              Consider cleaning up old galleries to reclaim space.
            </Text>
          </Alert>
        )}

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
          {/* Quick Actions */}
          <Card bg={cardBg} borderColor={borderColor}>
            <CardHeader>
              <Heading size="md">Quick Actions</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4}>
                <Button
                  as={RouterLink}
                  to="/admin/gallery"
                  leftIcon={<AddIcon />}
                  colorScheme="blue"
                  size="lg"
                  w="100%"
                >
                  Create New Gallery
                </Button>
                
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="100%">
                  <Button
                    as={RouterLink}
                    to="/admin/galleries"
                    leftIcon={<ViewIcon />}
                    variant="outline"
                    size="md"
                  >
                    View All Galleries
                  </Button>
                  
                  <Button
                    as={RouterLink}
                    to="/admin/cleanup"
                    leftIcon={<DeleteIcon />}
                    variant="outline"
                    colorScheme="orange"
                    size="md"
                  >
                    Gallery Cleanup
                  </Button>
                </SimpleGrid>
              </VStack>
            </CardBody>
          </Card>

          {/* Admin Tools */}
          <Card bg={cardBg} borderColor={borderColor}>
            <CardHeader>
              <Heading size="md">Admin Tools</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={3}>
                <Button
                  as={RouterLink}
                  to="/admin/galleries"
                  leftIcon={<ViewIcon />}
                  variant="ghost"
                  w="100%"
                  justifyContent="flex-start"
                >
                  Manage All Galleries
                </Button>
                
                <Button
                  as={RouterLink}
                  to="/admin/cleanup"
                  leftIcon={<TimeIcon />}
                  variant="ghost"
                  w="100%"
                  justifyContent="flex-start"
                >
                  Expiration & Cleanup
                </Button>
                
                <VStack spacing={2} w="100%">
                  <Text fontSize="sm" fontWeight="bold" alignSelf="flex-start">
                    📧 Test Email Service
                  </Text>
                  <HStack w="100%">
                    <Input
                      placeholder="test@email.com"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      size="sm"
                    />
                    <Button
                      onClick={handleTestEmail}
                      isLoading={testingEmail}
                      loadingText="Sending..."
                      size="sm"
                      colorScheme="blue"
                    >
                      Test
                    </Button>
                  </HStack>
                </VStack>
                
                <Button
                  leftIcon={<DownloadIcon />}
                  variant="ghost"
                  w="100%"
                  justifyContent="flex-start"
                  isDisabled
                >
                  Export Data (Coming Soon)
                </Button>
                
                <Button
                  leftIcon={<SettingsIcon />}
                  variant="ghost"
                  w="100%"
                  justifyContent="flex-start"
                  isDisabled
                >
                  System Settings (Coming Soon)
                </Button>
              </VStack>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Recent Galleries */}
        <Card bg={cardBg} borderColor={borderColor}>
          <CardHeader>
            <Flex justify="space-between" align="center">
              <Heading size="md">Recent Galleries</Heading>
              <Button
                as={RouterLink}
                to="/admin/galleries"
                size="sm"
                variant="outline"
              >
                View All
              </Button>
            </Flex>
          </CardHeader>
          <CardBody>
            {recentGalleries.length === 0 ? (
              <Alert status="info">
                <AlertIcon />
                No galleries created yet. Create your first gallery to get started!
              </Alert>
            ) : (
              <VStack spacing={4}>
                {recentGalleries.map((gallery) => (
                  <Box
                    key={gallery.id}
                    p={4}
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="md"
                    w="100%"
                  >
                    <Flex justify="space-between" align="center">
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="bold">{gallery.title}</Text>
                        <Text color="gray.600" fontSize="sm">
                          {gallery.client_name} • €{gallery.price_per_image} per image
                        </Text>
                        <Text color="gray.500" fontSize="xs">
                          Created {formatDate(gallery.created_at)}
                          {gallery.expires_at && ` • Expires ${formatDate(gallery.expires_at)}`}
                        </Text>
                      </VStack>
                      
                      <VStack align="end" spacing={2}>
                        {getGalleryStatus(gallery)}
                        <Button
                          as="a"
                          href={`/client/${gallery.token}`}
                          target="_blank"
                          size="sm"
                          leftIcon={<ViewIcon />}
                        >
                          View Gallery
                        </Button>
                      </VStack>
                    </Flex>
                  </Box>
                ))}
              </VStack>
            )}
          </CardBody>
        </Card>

      </VStack>
      </Box>
    </AdminAuth>
  );
};

export default AdminDashboard;